/**
 * The parts of a call that need no browser: what to do with a candidate that
 * arrives early, who has left, and how to read a peer's state. Kept apart
 * from the hook so they can be tested without an RTCPeerConnection.
 */

import { isCallDeviceKind, type Call, type CallDeviceKind, type UUID } from '@/api/types'

/** Who is in the call besides you, according to the server. */
export function joinedPeerIds(call: Call, selfId: UUID | undefined): UUID[] {
  return call.participants
    .filter((participant) => participant.status === 'joined')
    .map((participant) => participant.user?.id)
    .filter((id): id is UUID => typeof id === 'string' && id !== selfId)
}

/** The session a participant's seat is on, as the call we hold says. */
export function participantSessionId(call: Call | null, userId: UUID): string | null {
  if (call === null) return null
  return call.participants.find((participant) => participant.user?.id === userId)?.session_id ?? null
}

/**
 * Peers whose person is now in the call from a different device than the one
 * we are connected to (calls-v1 §4.3). The connection held goes to a device
 * that has left; it is closed, and the new device's offer makes the next one.
 */
export function replacedPeerIds(
  held: ReadonlyMap<UUID, string | null>,
  call: Call,
  selfId: UUID | undefined,
): UUID[] {
  return call.participants.flatMap((participant) => {
    const id = participant.user?.id
    if (id === undefined || id === selfId || participant.status !== 'joined') return []
    const seat = participant.session_id ?? null
    const known = held.get(id)
    return seat !== null && known !== undefined && known !== null && known !== seat ? [id] : []
  })
}

/** An ICE candidate as it goes out: calls-v1 §16, nothing more. */
export type IceCandidatePayload = {
  candidate: { candidate: string; sdpMid: string | null; sdpMLineIndex: number | null }
}

export function iceCandidatePayload(candidate: RTCIceCandidateInit): IceCandidatePayload {
  return {
    candidate: {
      candidate: candidate.candidate ?? '',
      sdpMid: candidate.sdpMid ?? null,
      sdpMLineIndex: candidate.sdpMLineIndex ?? null,
    },
  }
}

/**
 * An ICE candidate as it comes in. The nested shape is the contract; Android
 * 1.6 and older send the three fields flat, with the SDP line as a string
 * under `candidate`, and web used to drop those on the floor — which is why a
 * browser and an Android phone never connected.
 */
export function readIceCandidate(payload: unknown): RTCIceCandidateInit | null {
  if (typeof payload !== 'object' || payload === null) return null
  const body = payload as Record<string, unknown>
  const inner = body['candidate']
  const source =
    typeof inner === 'object' && inner !== null
      ? (inner as Record<string, unknown>)
      : typeof inner === 'string'
        ? body
        : null
  if (source === null) return null

  const line = source['candidate']
  const mid = source['sdpMid']
  const index = source['sdpMLineIndex']
  if (typeof line !== 'string') return null
  return {
    candidate: line,
    sdpMid: typeof mid === 'string' ? mid : null,
    sdpMLineIndex: typeof index === 'number' ? index : null,
  }
}

/**
 * Peers we hold a connection to who are no longer joined. Their connection
 * is closed and their card removed; until this existed a departed
 * participant stayed on screen, frozen, for the rest of the call.
 */
export function departedPeerIds(connected: Iterable<UUID>, call: Call, selfId: UUID | undefined): UUID[] {
  const stillIn = new Set(joinedPeerIds(call, selfId))
  return [...connected].filter((id) => !stillIn.has(id))
}

export type PeerState = 'connecting' | 'connected' | 'reconnecting' | 'failed'

/**
 * One word per peer. `connected` is the only state in which media is known to
 * be flowing; the channel having joined says nothing about that, which is
 * why "Connected" used to appear over a call with no sound in it.
 */
export function describePeer(state: RTCPeerConnectionState | undefined): PeerState {
  switch (state) {
    case 'connected':
      return 'connected'
    case 'disconnected':
      return 'reconnecting'
    case 'failed':
    case 'closed':
      return 'failed'
    default:
      return 'connecting'
  }
}

/** What the header says about the call as a whole. */
export function describeCall(peers: Record<UUID, RTCPeerConnectionState>, channelJoined: boolean): string {
  const states = Object.values(peers).map(describePeer)
  if (!channelJoined) return 'Connecting…'
  if (states.length === 0) return 'Waiting for the others to join'
  if (states.every((state) => state === 'connected')) return 'Connected'
  if (states.some((state) => state === 'failed')) return 'Could not reach everyone'
  if (states.some((state) => state === 'reconnecting')) return 'Reconnecting…'
  return 'Connecting…'
}

/** What a refused `call:<id>` join said, as far as it said anything (calls-v1 §6.2). */
export function readJoinRefusal(reply: unknown): { code: string | null; deviceKind: CallDeviceKind | null } {
  const error = typeof reply === 'object' && reply !== null ? (reply as { error?: unknown }).error : undefined
  if (typeof error !== 'object' || error === null) return { code: null, deviceKind: null }
  const code = (error as { code?: unknown }).code
  // The server puts who holds the seat under `details`, like every other
  // error envelope; a flat `device_kind` is read too, in case one ever arrives.
  const details = (error as { details?: unknown }).details
  const deviceKind =
    typeof details === 'object' && details !== null && 'device_kind' in details
      ? (details as { device_kind?: unknown }).device_kind
      : (error as { device_kind?: unknown }).device_kind
  return {
    code: typeof code === 'string' ? code : null,
    deviceKind: isCallDeviceKind(deviceKind) ? deviceKind : null,
  }
}

/** How long a new connection gets before an ICE restart, and again before it is called unreachable. */
export const PEER_CONNECT_TIMEOUT_MS = 30_000

export interface PeerWatchdogEvents {
  restart(peerId: UUID): void
  unreachable(peerId: UUID): void
}

/**
 * The clock on a connection that has not come up (calls-v1 §7.6). A peer that
 * is not `connected` 30 seconds after it was made gets one ICE restart, and
 * 30 seconds after that its tile says it cannot be reached. A `failed` state
 * skips whichever wait it is in: the browser has already given up. The first
 * client waited for ever, and a call nobody answered looked exactly like a
 * call about to start.
 */
export class PeerWatchdog {
  private readonly watched = new Map<UUID, { timer: ReturnType<typeof setTimeout>; restarted: boolean }>()

  constructor(
    private readonly events: PeerWatchdogEvents,
    private readonly timeoutMs = PEER_CONNECT_TIMEOUT_MS,
  ) {}

  /** A connection was made, or made again: the clock starts from nothing. */
  watch(peerId: UUID): void {
    this.forget(peerId)
    this.arm(peerId, false)
  }

  connected(peerId: UUID): void {
    this.forget(peerId)
  }

  failed(peerId: UUID): void {
    if (!this.watched.has(peerId)) this.arm(peerId, false)
    this.expire(peerId)
  }

  forget(peerId: UUID): void {
    const entry = this.watched.get(peerId)
    if (entry !== undefined) clearTimeout(entry.timer)
    this.watched.delete(peerId)
  }

  stop(): void {
    for (const peerId of [...this.watched.keys()]) this.forget(peerId)
  }

  private arm(peerId: UUID, restarted: boolean): void {
    const timer = setTimeout(() => this.expire(peerId), this.timeoutMs)
    this.watched.set(peerId, { timer, restarted })
  }

  private expire(peerId: UUID): void {
    const entry = this.watched.get(peerId)
    if (entry === undefined) return
    clearTimeout(entry.timer)
    if (entry.restarted) {
      this.watched.delete(peerId)
      this.events.unreachable(peerId)
      return
    }
    this.arm(peerId, true)
    this.events.restart(peerId)
  }
}

/** Fetch ICE servers again this long before the credentials in hand expire. */
export const ICE_REFRESH_LEAD_MS = 30_000
const ICE_REFRESH_FLOOR_MS = 10_000

/**
 * How long until the ICE servers are fetched again (calls-v1 §7.8). TURN
 * credentials last five minutes; a meeting lasts longer, and an ICE restart
 * after they lapse finds a relay that no longer lets it in. Never sooner than
 * ten seconds, so a clock that disagrees with the server's cannot turn this
 * into a loop.
 */
export function iceRefreshDelay(expiresAt: string, now: number): number | null {
  const expires = Date.parse(expiresAt)
  if (!Number.isFinite(expires)) return null
  return Math.max(ICE_REFRESH_FLOOR_MS, expires - now - ICE_REFRESH_LEAD_MS)
}
